import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["timeRanges", "blockedDates"],
  _excluded2 = ["timeRanges", "blockedDates"];
import { List } from 'immutable';
// @ts-expect-error untyped module
import * as SimpleDate from 'UIComponents/core/SimpleDate';
import BlockedDateMetadata from 'automation-ui-canvas/flow/records/properties/Timing/BlockedDateMetadata';
import TimeRangeMetadata from 'automation-ui-canvas/flow/records/properties/Timing/TimeRangeMetadata';
import { DayOfWeekArray } from 'automation-ui-canvas/constants/DayOfWeek';
import { DayOfWeekPresets, DaysOfWeekByPresets } from 'automation-ui-canvas/flow/constants/DayOfWeekPresets';
import { isDayOfWeekPreset } from 'automation-ui-canvas/types/DayOfWeekPresets';
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
// @ts-expect-error untyped module
import { now, compare } from 'UIComponents/core/SimpleDate';
export function getNextAvailableDate(blockedDates) {
  const today = SimpleDate.now();
  let lastDate = today;
  if (blockedDates.size) {
    const dateSet = new Set(
    // @ts-expect-error This is using a JS set and not immutable set
    blockedDates.map(blockedDate => blockedDate.getDate().toString()));
    let date = SimpleDate.nextDay(blockedDates.last().getDate());
    if (SimpleDate.compare(date, today) <= -1) {
      date = today;
    }
    while (dateSet.has(date.toString())) {
      date = SimpleDate.nextDay(date);
    }
    lastDate = date;
  }
  return lastDate;
}
export default class TimeWindowMetadata extends createImmutableObjectClass({
  timeRanges: [],
  blockedDates: []
}, {
  fromJSON: _ref => {
    let {
        timeRanges,
        blockedDates
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({
      timeRanges: List(timeRanges.map(timeRange => new TimeRangeMetadata(timeRange))),
      blockedDates: List((blockedDates || []).map(blockedDate => new BlockedDateMetadata(blockedDate))).sort((blockedDateA, blockedDateB) => {
        return SimpleDate.compare(blockedDateA.getDate(), blockedDateB.getDate());
      })
    }, rest);
  },
  toJSON: _ref2 => {
    let {
        timeRanges,
        blockedDates
      } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    return Object.assign({}, rest, {
      timeRanges: timeRanges.toArray().map(timeRange => timeRange.toJSON()),
      blockedDates: blockedDates.toArray().map(blockedDate => blockedDate.toJSON())
    });
  }
}) {
  // timeRanges
  handleDefaultDay(day) {
    if (isDayOfWeekPreset(day)) {
      return 'MONDAY';
    }
    const nextDayIndex = (DayOfWeekArray.indexOf(day) + 1) % DayOfWeekArray.length;
    return DayOfWeekArray[nextDayIndex];
  }
  addTimeRange() {
    const nineAMInMinutes = 9 * 60;
    const fivePMInMinutes = 17 * 60;
    const currentTimeRanges = this.getTimeRanges();
    const lastTimeRange = currentTimeRanges.last();
    const defaultDay = lastTimeRange ? this.handleDefaultDay(lastTimeRange.getDay()) : DayOfWeekPresets.EVERY_DAY;
    const defaultTimeRange = new TimeRangeMetadata({}).updateDay(defaultDay).updateStartTime(lastTimeRange ? lastTimeRange.getStartTimeInMinutes() + 30 : nineAMInMinutes).updateEndTime(lastTimeRange ? lastTimeRange.getEndTimeInMinutes() + 30 : fivePMInMinutes);
    return this.set('timeRanges', this.getTimeRanges().push(defaultTimeRange));
  }
  removeTimeRange(index) {
    return this.set('timeRanges', this.getTimeRanges().delete(index));
  }
  updateTimeRangeStartTime(index, minutes) {
    return this.set('timeRanges', this.getTimeRanges().update(index, timeRange => timeRange.updateStartTime(minutes)));
  }
  updateTimeRangeEndTime(index, minutes) {
    return this.set('timeRanges', this.getTimeRanges().update(index, timeRange => timeRange.updateEndTime(minutes)));
  }
  updateTimeRangeDay(index, day) {
    return this.set('timeRanges', this.getTimeRanges().update(index, timeRange => timeRange.updateDay(day)));
  }
  getStartTimeInMinutes(index) {
    const startTimeInMinutes = this.getTimeRanges().get(index).getStartTimeInMinutes();
    return startTimeInMinutes;
  }
  getEndTimeInMinutes(index) {
    const endTimeInMinutes = this.getTimeRanges().get(index).getEndTimeInMinutes();
    return endTimeInMinutes;
  }
  getDay(index) {
    const day = this.getTimeRanges().get(index).getDay();
    return day;
  }

  // Simplify time ranges to the quick presets where possible. IE turns mon - sun with the same date and time into "Every day"
  getTimeRanges() {
    // The new time ranges that have replaced a set of time ranges
    const simplifiedTimeRanges = [];

    // Time ranges that have not been simplified
    const remainingTimeRanges = [];
    const timeRanges = this.getTimeRangesWithDayPresets(this.timeRanges);
    timeRanges.forEach(timeRange => {
      // Begin by assuming the time range is not part of a preset
      remainingTimeRanges.push(timeRange);
      for (const presetValue of Object.values(DayOfWeekPresets)) {
        /* is the current time range in the preset we are matching */
        const presetContainsRange = DaysOfWeekByPresets[presetValue].some(presetDay => presetDay === timeRange.getDay());

        /* is there a match for each day in the preset */
        const hasPresetMatch = presetContainsRange && DaysOfWeekByPresets[presetValue].every(presetDay => timeRanges.some(tr => tr.getDay() === presetDay && tr.getStartTimeInMinutes() === timeRange.getStartTimeInMinutes() && tr.getEndTimeInMinutes() === timeRange.getEndTimeInMinutes()));
        if (hasPresetMatch && presetContainsRange) {
          if (
          // Avoid inserting the same preset multiple times
          !simplifiedTimeRanges.find(tr => tr.getDay() === presetValue && tr.getStartTimeInMinutes() === timeRange.getStartTimeInMinutes() && tr.getEndTimeInMinutes() === timeRange.getEndTimeInMinutes())) {
            simplifiedTimeRanges.push(new TimeRangeMetadata({
              day: presetValue,
              startTime: timeRange.startTime,
              endTime: timeRange.endTime
            }));
          }
          // If the time range is part of a preset do not include it in the list of time ranges
          remainingTimeRanges.pop();

          // Only consider the first preset match to avoid one time range being represented by multiple presets. Precedence is given to the first preset match
          break;
        }
      }
    });
    return List([...simplifiedTimeRanges, ...remainingTimeRanges]);
  }
  hasEmptyTimeRanges() {
    return this.getTimeRanges().size === 0;
  }
  clearTimeRanges() {
    return this.set('timeRanges', List());
  }
  getQuickOptionDays() {
    const timeRanges = this.getTimeRanges();
    const quickOptionDays = [];
    timeRanges.forEach(timeRange => {
      const day = timeRange.getDay();
      if (DayOfWeekPresets[day]) {
        quickOptionDays.push(day);
      }
    });
    return quickOptionDays;
  }
  getTimeRangesWithDayPresets(timeRanges) {
    const newTimeRanges = [];
    timeRanges.forEach(timeRange => {
      const day = timeRange.getDay();
      const daySet = DaysOfWeekByPresets[day] || [day];
      daySet.forEach(dayEle => {
        const newTimeRange = timeRange.updateDay(dayEle);
        newTimeRanges.push(newTimeRange);
      });
    });
    return newTimeRanges;
  }

  // blockedDates
  getDefaultBlockedDate() {
    const blockedDates = this.get('blockedDates');
    return getNextAvailableDate(blockedDates);
  }
  addBlockedDate() {
    const defaultDate = new BlockedDateMetadata().setDate(this.getDefaultBlockedDate());
    return this.update('blockedDates', blockedDates => blockedDates.push(defaultDate));
  }
  removeBlockedDate(index) {
    return this.update('blockedDates', dates => dates.delete(index));
  }
  setBlockedDate(index, blockedDate) {
    return this.update('blockedDates', blockedDates => blockedDates.set(index, blockedDate));
  }
  getBlockedDate(index) {
    return this.blockedDates.get(index);
  }
  getBlockedDates() {
    return this.blockedDates;
  }
  getFutureBlockedDates() {
    return this.blockedDates.filter(dateObj => dateObj.type === 'RECURRING_DATE' || dateObj.getDate() && compare(dateObj.getDate(), now()) !== -1);
  }
  getIsValid() {
    const blockedDates = this.getBlockedDates();
    return blockedDates.every(blockedDate => blockedDate.getDate() !== null);
  }
  requestCompatible() {
    const timeRanges = this.getTimeRanges();
    const blockedDates = this.getBlockedDates();
    return this.set('timeRanges', List(this.getTimeRangesWithDayPresets(timeRanges))).set('blockedDates', blockedDates);
  }
}